import { computed, shallowRef, type ComputedRef } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';

/*****************************************************************************
 * The service worker's update state, shared by the two bottom-left overlays.
 *
 * PwaUpdatePrompt.vue registers the worker and asks to update. The install
 * card (PwaInstallCard.vue) sits in the same corner, so it waits while an
 * update is pending and appears once the member picks "Later". Each used to
 * know nothing of the other, and the prompt (z-50) covered the card's buttons.
 *
 * useRegisterSW registers the worker and owns `needRefresh`, so it is called
 * once per page load and every reader shares that one result.
 *****************************************************************************/

type ServiceWorkerUpdate = ReturnType< typeof useRegisterSW >;

const registered = shallowRef< ServiceWorkerUpdate | null >( null );
let registration: ServiceWorkerRegistration | undefined;

/**
 * Registers the service worker on the first call and returns the shared update state. For PwaUpdatePrompt, which owns
 * the registration timing and the reloads; anything that only needs to know whether an update is pending reads
 * `isUpdatePending`, which never registers.
 */
export function usePwaUpdate(): ServiceWorkerUpdate {
    // UX roadmap #27: no `immediate`, so workbox-window registers after the window's `load` event. With it the worker
    // installed during App setup and a first visit's precache download raced the route's chunk and first API calls.
    registered.value ??= useRegisterSW( {
        onRegisteredSW( _swUrl, swRegistration ) {
            registration = swRegistration;
        },
    } );
    return registered.value;
}

/** The worker's registration once workbox-window has registered it, for the update poll. */
export function serviceWorkerRegistration(): ServiceWorkerRegistration | undefined {
    return registration;
}

/** A new build is waiting, so the update prompt is up (or will be once a sign-in finishes). False until registration. */
export const isUpdatePending: ComputedRef< boolean > = computed( () => registered.value?.needRefresh.value ?? false );
